import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircleOutline, ArrowForward } from '@mui/icons-material';
import '../styles/SeoPages.css';
import '../styles/common.css';

const VerifyHuidPage = () => {
    const [huid, setHuid] = useState('');
    const [error, setError] = useState('');

    const HUID_REGEX = /^[A-Za-z0-9]{6}$/;

    const handleVerify = () => {
        const trimmed = huid.trim().toUpperCase();
        if (!trimmed) { setError('Please enter an HUID code.'); return; }
        if (!HUID_REGEX.test(trimmed)) { setError('HUID must be exactly 6 alphanumeric characters.'); return; }
        setError('');
        window.open(`https://www.bis.gov.in/index.php/hallmark/huid-consumer-check/`, '_blank', 'noopener,noreferrer');
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            { "@type": "Question", "name": "What is HUID in gold jewellery?", "acceptedAnswer": { "@type": "Answer", "text": "HUID stands for Hallmark Unique Identification. It is a 6-digit alphanumeric code assigned by BIS (Bureau of Indian Standards) to every hallmarked gold jewellery piece, ensuring its purity and authenticity." } },
            { "@type": "Question", "name": "How do I verify HUID online?", "acceptedAnswer": { "@type": "Answer", "text": "You can verify an HUID online by visiting the official BIS consumer portal at www.bis.gov.in and using the HUID consumer check feature. Enter the 6-character code printed on your jewellery." } },
            { "@type": "Question", "name": "Is HUID mandatory for gold jewellery in India?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, since April 2023 HUID-based hallmarking is mandatory for all gold jewellery sold in India under BIS regulations. Every piece must carry a BIS logo, purity grade, and unique HUID code." } },
            { "@type": "Question", "name": "Where is HUID printed on jewellery?", "acceptedAnswer": { "@type": "Answer", "text": "The HUID is laser-engraved or stamped on the jewellery piece. It appears along with the BIS hallmark logo and purity marks (like 22K or 916)." } },
            { "@type": "Question", "name": "Does MAB Jewels provide HUID-certified jewellery?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, MAB Jewels (Manoj Abhushan Bhandar) sells BIS-hallmarked gold jewellery with HUID codes, ensuring government-verified purity for every piece." } }
        ]
    };

    const webAppSchema = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "HUID Verifier – MAB Jewels",
        "url": "https://mabjewels.com/verify-huid",
        "description": "Verify the authenticity of your BIS hallmarked gold jewellery using the HUID (Hallmark Unique Identification) code.",
        "applicationCategory": "UtilitiesApplication",
        "operatingSystem": "All"
    };

    return (
        <div className="page-container seo-page">
            <Helmet>
                <title>HUID Verifier – Check BIS Hallmark Gold Purity Online | MAB Jewels</title>
                <meta name="description" content="Verify your gold jewellery's BIS hallmark HUID online. Enter the 6-digit HUID code to check authenticity, purity & certification on the official BIS portal." />
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
                <script type="application/ld+json">{JSON.stringify(webAppSchema)}</script>
            </Helmet>

            <div className="seo-content-wrapper">
                <h1>HUID Verifier – Check BIS Hallmark Gold Authenticity</h1>
                <p className="last-updated-tag" style={{ fontSize: '0.9rem', color: '#666', marginTop: '-10px', marginBottom: '20px' }}>
                    Tool powered by BIS (Bureau of Indian Standards) | Government of India
                </p>

                <p className="seo-intro">
                    Every BIS-hallmarked gold jewellery piece in India now carries a unique <strong>HUID (Hallmark Unique Identification)</strong> code. Use this tool to verify whether your gold is government-certified and meets BIS purity standards.
                </p>

                {/* Verifier Tool */}
                <div style={{ background: 'linear-gradient(135deg, #fffbea, #fff8e1)', border: '1px solid #d4af37', borderRadius: '12px', padding: '28px', margin: '28px 0' }}>
                    <h2 style={{ marginTop: 0, color: '#8b6914' }}>Enter Your HUID Code</h2>
                    <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '16px' }}>
                        Find the 6-character alphanumeric HUID code engraved on your jewellery piece. It appears alongside the BIS hallmark logo.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <input
                                type="text"
                                value={huid}
                                onChange={e => { setHuid(e.target.value.toUpperCase()); setError(''); }}
                                placeholder="e.g. A3B2C1"
                                maxLength={6}
                                style={{
                                    width: '100%', padding: '13px 16px', fontSize: '1.1rem', fontWeight: '600',
                                    letterSpacing: '3px', textTransform: 'uppercase', border: '2px solid #d4af37',
                                    borderRadius: '8px', outline: 'none', boxSizing: 'border-box',
                                    fontFamily: 'monospace', background: '#fff'
                                }}
                            />
                            {error && <p style={{ color: '#c0392b', fontSize: '0.85rem', marginTop: '6px' }}>{error}</p>}
                        </div>
                        <button
                            onClick={handleVerify}
                            style={{
                                background: 'linear-gradient(135deg, #d4af37, #b8960c)', color: '#fff',
                                border: 'none', borderRadius: '8px', padding: '13px 24px', fontSize: '1rem',
                                fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap'
                            }}
                        >
                            Verify on BIS Portal
                        </button>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '14px', marginBottom: 0 }}>
                        You will be redirected to the official BIS government portal to complete verification.
                    </p>
                </div>

                <h2>What is HUID in Gold Jewellery?</h2>
                <p>
                    HUID stands for <strong>Hallmark Unique Identification</strong>. It is a <strong>6-character alphanumeric code</strong> assigned by the Bureau of Indian Standards (BIS) to every piece of hallmarked gold jewellery in India. The HUID is laser-engraved on the jewellery and links to a central BIS database containing details of:
                </p>
                <ul className="seo-check-list">
                    <li>Gold purity (e.g. 22K / 916)</li>
                    <li>Name of the Assaying & Hallmarking Centre (AHC)</li>
                    <li>Jeweller details and registration</li>
                    <li>Year of hallmarking</li>
                </ul>

                <h2>How to Verify HUID Online</h2>
                <ol style={{ lineHeight: '1.9' }}>
                    <li>Locate the 6-character HUID code engraved on your gold jewellery.</li>
                    <li>Enter the code in the tool above and click <strong>Verify on BIS Portal</strong>.</li>
                    <li>On the BIS portal, enter your HUID code in the consumer check form.</li>
                    <li>The portal will display full certification details for that piece.</li>
                </ol>

                <h2>Why HUID Matters for Gold Buyers</h2>
                <ul className="seo-check-list">
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Proves purity</strong> — No fake 22K claims. Government database confirms the actual purity.</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Protects resale value</strong> — HUID-certified jewellery is accepted at full value by jewellers.</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Prevents fraud</strong> — Each HUID is unique and cannot be duplicated.</li>
                    <li><CheckCircleOutline sx={{ color: '#d4af37', mr: 1, verticalAlign: 'middle' }} /> <strong>Mandatory since April 2023</strong> — BIS has made HUID-based hallmarking compulsory across India.</li>
                </ul>

                <h2>Does MAB Jewels Provide HUID-Certified Gold?</h2>
                <p>
                    Yes. At <strong>MAB Jewels (Manoj Abhushan Bhandar)</strong> in Jodhpur, every gold jewellery piece we sell is <strong>BIS-hallmarked with an HUID code</strong>. When you purchase from us, you receive:
                </p>
                <ul className="seo-check-list">
                    <li>A piece with a unique HUID engraved on it</li>
                    <li>Full BIS certification verifiable online</li>
                    <li>Government-guaranteed purity assurance</li>
                </ul>
                <p>
                    You can verify any piece purchased from us using the tool above. If you would like to check a specific item from our collection, <Link to="/jewellery">browse our jewellery</Link> — HUID-verified pieces are marked with a badge.
                </p>

                <h2>Frequently Asked Questions (FAQs)</h2>
                <div className="seo-faq-section">
                    <div className="seo-faq-item">
                        <h4>What is HUID in gold jewellery?</h4>
                        <p>HUID (Hallmark Unique Identification) is a 6-character alphanumeric code assigned by BIS to every hallmarked gold jewellery piece in India, ensuring its purity and authenticity.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>How do I verify HUID online?</h4>
                        <p>Enter the 6-character HUID code from your jewellery in the tool above and click "Verify on BIS Portal". You will be directed to the official BIS consumer check portal.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Is HUID mandatory for gold jewellery in India?</h4>
                        <p>Yes. Since April 2023, HUID-based hallmarking is mandatory for all gold jewellery sold in India under BIS regulations.</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Where is HUID printed on jewellery?</h4>
                        <p>The HUID is laser-engraved or stamped on the jewellery piece alongside the BIS hallmark logo and purity marks (like 22K or 916).</p>
                    </div>
                    <div className="seo-faq-item">
                        <h4>Does MAB Jewels provide HUID-certified jewellery?</h4>
                        <p>Yes, MAB Jewels sells BIS-hallmarked gold jewellery with HUID codes, ensuring government-verified purity for every piece.</p>
                    </div>
                </div>

                <div className="internal-links-section" style={{ marginTop: '40px', padding: '20px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>Explore More from MAB Jewels</h3>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/jewellery">Browse HUID-Verified Gold Jewellery</Link>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/bis-hallmarking-guide">BIS Hallmarking Guide</Link>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/gold-rate-jodhpur">Gold Rate in Jodhpur Today</Link>
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                            <ArrowForward sx={{ color: '#d4af37', mr: 1 }} />
                            <Link to="/how-to-check-gold-purity-rajasthan">How to Check Gold Purity</Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default VerifyHuidPage;
